import { get } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import HotelStrategyStrategyDetailWithTabsBaseController from 'lumen/controllers/hotel/strategy/strategy-detail-with-tabs-base';
import {
  COLUMN_PROPERTIES,
  SUBTYPE_VALUES_KEY_CANCELLATION_POLICY,
  SUBTYPE_VALUES_KEY_STRATEGY,
  TYPE_ID_BEST_FLEX,
  TYPE_ID_CANCELLATION,
  TYPE_ID_NON_REF,
  TYPE_ID_SEMI_FLEX,
} from 'lumen/utils/constants/rate-strategy-constants';
import { formatTableRate } from 'lumen/utils/strategy/detail-view-helpers';
import { timeout } from 'lumen/utils/waiters';

export default class HotelStrategyCancellationController extends HotelStrategyStrategyDetailWithTabsBaseController {
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service simpleExport;

  @tracked entity = SUBTYPE_VALUES_KEY_CANCELLATION_POLICY;

  typeId = TYPE_ID_CANCELLATION;

  exportRSTableTask = task({ drop: true }, async () => {
    const sheetName = this.intl
      .t('strategy.section_title.cancellation_policy')
      .toString();

    const headers = [
      this.intl.t('general.hotels').toString(),
      `${this.intl.t('strategy.metric_block.cutoff_policy')} (${this.intl.t('rates.best_flex')})`,
      `${this.intl.t('rates.rate')} (${this.intl.t('rates.best_flex')})`,
    ];

    if (this.cancellationPolicyKeys.indexOf(TYPE_ID_SEMI_FLEX) !== -1) {
      headers.push(
        `${this.intl.t('strategy.metric_block.cutoff_policy')} (${this.intl.t('rates.semi_flex')})`,
      );
      headers.push(
        `${this.intl.t('rates.rate')} (${this.intl.t('rates.semi_flex')})`,
      );
      headers.push(
        `${this.intl.t('strategy.metric_block.discount')} (${this.intl.t('rates.semi_flex')})`,
      );
    }

    if (this.cancellationPolicyKeys.indexOf(TYPE_ID_NON_REF) !== -1) {
      headers.push(
        `${this.intl.t('rates.rate')} (${this.intl.t('rates.nonrefundable')})`,
      );
      headers.push(
        `${this.intl.t('strategy.metric_block.discount')} (${this.intl.t('rates.nonrefundable')})`,
      );
    }

    const content = [];
    this.cancellationPolicyPerHotelInfo.forEach((rowObject) => {
      const row = [
        rowObject[COLUMN_PROPERTIES.HOTELINFO].formattedValue,
        rowObject[TYPE_ID_BEST_FLEX][COLUMN_PROPERTIES.CUT_OFF_POLICY]
          .formattedValue,
        formatTableRate(rowObject[TYPE_ID_BEST_FLEX][COLUMN_PROPERTIES.RATE]),
      ];

      if (this.cancellationPolicyKeys.indexOf(TYPE_ID_SEMI_FLEX) !== -1) {
        row.push(
          rowObject[TYPE_ID_SEMI_FLEX][COLUMN_PROPERTIES.CUT_OFF_POLICY]
            .formattedValue,
        );
        row.push(
          formatTableRate(rowObject[TYPE_ID_SEMI_FLEX][COLUMN_PROPERTIES.RATE]),
        );
        row.push(
          rowObject[TYPE_ID_SEMI_FLEX][COLUMN_PROPERTIES.DISCOUNT]
            .formattedValue,
        );
      }

      if (this.cancellationPolicyKeys.indexOf(TYPE_ID_NON_REF) !== -1) {
        row.push(
          formatTableRate(rowObject[TYPE_ID_NON_REF][COLUMN_PROPERTIES.RATE]),
        );
        row.push(
          rowObject[TYPE_ID_NON_REF][COLUMN_PROPERTIES.DISCOUNT].formattedValue,
        );
      }

      content.push(row);
    });

    const postData = JSON.stringify({ headers, content, sheetName });
    this.simpleExport.execute(postData, this.hotelAndMarketService.hotel?.id);

    // Add a cooldown before the export can be triggered again
    // Prevents multiple downloads on double click or worse: Chrome disabling downloads from mylighthouse.com
    await timeout();
  });

  get cancellationPolicyKeys() {
    const cancellationPolicyKeys = [TYPE_ID_BEST_FLEX];
    if (this.typesDataPerId[TYPE_ID_SEMI_FLEX]) {
      cancellationPolicyKeys.push(TYPE_ID_SEMI_FLEX);
    }
    if (this.typesDataPerId[TYPE_ID_NON_REF]) {
      cancellationPolicyKeys.push(TYPE_ID_NON_REF);
    }
    return cancellationPolicyKeys;
  }

  get cancellationPolicyPerHotelInfo() {
    const cancellationPolicyPerHotelInfo = new Map();
    if (this.hasRSData) {
      // Initialize Map
      this.selectedCompetitors.forEach((hotelinfo) => {
        cancellationPolicyPerHotelInfo.set(hotelinfo.id, {
          [COLUMN_PROPERTIES.HOTELINFO]: {
            id: hotelinfo.id,
            formattedValue: hotelinfo.name,
            isOwnHotelInfo:
              this.hotelAndMarketService.isOwnHotelInfo(hotelinfo),
          },
          // Add a dummy object for each subtype (corresponds to a grouped column in the detail view)
          [TYPE_ID_BEST_FLEX]: {},
          [TYPE_ID_SEMI_FLEX]: {},
          [TYPE_ID_NON_REF]: {},
        });
      });

      this.cancellationPolicyKeys.forEach((cancellationPolicyKey) => {
        this.typesDataPerId[cancellationPolicyKey]?.filteredSubtypes.forEach(
          (subtype) => {
            subtype.filteredCancellationpolicies.forEach(
              (cancellationPolicy) => {
                cancellationPolicyPerHotelInfo.get(
                  cancellationPolicy.hotelinfo.id,
                )[cancellationPolicyKey][COLUMN_PROPERTIES.CUT_OFF_POLICY] = {
                  value: cancellationPolicy.days, // Actual value (used for sort),
                  timeValueForSorting: cancellationPolicy.timeValueForSorting, // value used for further sorting in case of same values
                  formattedValue: cancellationPolicy.formattedValueWithDetails, // Formatted value (used to display)
                  hasNoData: cancellationPolicy.hasNoData,
                  subsortOrder: cancellationPolicy.subsortOrder,
                };

                // The best_flex Price is fetched from the cancellationPolicy, semi_flex and non_refundable will be fetched from the strategy
                if (cancellationPolicyKey === TYPE_ID_BEST_FLEX) {
                  // Ember get necessary for Proxy objects
                  // eslint-disable-next-line ember/no-get
                  const baserate = get(cancellationPolicy, 'rsbaserate');
                  cancellationPolicyPerHotelInfo.get(
                    cancellationPolicy.hotelinfo.id,
                  )[cancellationPolicyKey][COLUMN_PROPERTIES.RATE] = {
                    // Ember Proxy Object: Since Ember 3.1, this is usually fine as you no longer need to use `.get()` to access computed properties. However, in this case, the object in question is a special kind of Ember object (a proxy). Therefore, it is still necessary to use `.get('rate')` in this case.
                    // eslint-disable-next-line ember/no-get
                    value: get(baserate, 'rate'),
                    formattedValue: cancellationPolicy.formattedRate,
                    baserate,
                    hasNoData: cancellationPolicy.hasNoData,
                    subsortOrder: cancellationPolicy.subsortOrder,
                  };
                }
              },
            );

            subtype.filteredStrategies.forEach((strategy) => {
              cancellationPolicyPerHotelInfo.get(strategy.hotelinfo.id)[
                cancellationPolicyKey
              ][COLUMN_PROPERTIES.RATE] = {
                value: strategy.discountedPriceWithFallback,
                formattedValue: strategy.formattedDiscountedPrice,
                baserate: strategy.baserateWithFallback,
                // A cancellation policy strategy can have no data but when we fall back on the baserate we don't want to handle it as "no data"
                hasNoData: strategy.discountedPriceWithFallback <= 0,
                subsortOrder: strategy.subsortOrder,
              };
              cancellationPolicyPerHotelInfo.get(strategy.hotelinfo.id)[
                cancellationPolicyKey
              ][COLUMN_PROPERTIES.DISCOUNT] = {
                value: strategy.supplementOrDiscount,
                formattedValue: strategy.formattedValue,
                hasNoData: strategy.hasNoData,
                subsortOrder: strategy.subsortOrder,
                isNonComputable: strategy.isNonComputable,
              };
            });
          },
        );
      });
    }

    return cancellationPolicyPerHotelInfo;
  }

  // Returns what data needs to be shown in the evolution chart (strategies or cancellation policies)
  get entityToDisplay() {
    // For Best Flex show cancellation policies
    if (this.subtypeId === TYPE_ID_BEST_FLEX) {
      return SUBTYPE_VALUES_KEY_CANCELLATION_POLICY;
    }

    // For Semi-flex both strategies and cancellation policies can be shown. Return the selected option in the dropdown.
    if (this.subtypeId === TYPE_ID_SEMI_FLEX) {
      return this.entity;
    }

    // For Non-refundable show strategies
    return SUBTYPE_VALUES_KEY_STRATEGY;
  }
}
